import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'

import { adminRoutes } from '@/features/admin/routes'
import { authRoutes } from '@/features/auth/routes'
import { configuratorRoutes } from '@/features/configurator/routes'
import { orderRoutes } from '@/features/orders/routes'
import { quoteRoutes } from '@/features/quotes/routes'
import { onAuthLost } from '@/shared/api'
import { useAuthStore } from '@/stores/auth'

import './meta'

const routes: RouteRecordRaw[] = [
	{ path: '/', redirect: { name: 'configurator' } },
	...authRoutes,
	...configuratorRoutes,
	...quoteRoutes,
	...orderRoutes,
	...adminRoutes,
	{
		path: '/:pathMatch(.*)*',
		name: 'not-found',
		component: () => import('@/features/auth/NotFoundPage.vue'),
		meta: { titleSk: 'Stránka sa nenašla' },
	},
]

export const router = createRouter({
	history: createWebHistory(),
	routes,
	scrollBehavior: (_to, _from, savedPosition) => savedPosition ?? { top: 0 },
})

/**
 * Stráž prístupu.
 *
 * Po tvrdom načítaní stránky ešte nevieme, či je používateľ prihlásený —
 * access token je len v pamäti. Preto sa najprv počká na `bootstrap()`
 * (obnovenie z refresh cookie) a až potom sa rozhoduje o presmerovaní;
 * inak by F5 na detaile objednávky vždy skončilo na prihlásení.
 */
router.beforeEach(async (to) => {
	const auth = useAuthStore()

	if (auth.initializing) {
		await auth.bootstrap()
	}

	if (to.meta.requiresAuth !== true) {
		return true
	}

	if (!auth.isAuthenticated) {
		return { name: 'login', query: { pokracovat: to.fullPath } }
	}

	if (to.meta.roles !== undefined && !auth.hasAnyRole(to.meta.roles)) {
		return { name: 'forbidden' }
	}

	return true
})

/** Titulok karty prehliadača — pomáha pri práci s viacerými ponukami naraz. */
router.afterEach((to) => {
	document.title = `${to.meta.titleSk} · MaxDoors`
})

/**
 * Otvorená karta po nasadení novej verzie.
 *
 * Obrazovky sa načítavajú až pri prechode na ne a ich súbory majú v názve
 * hash. Po nasadení tie staré zmiznú, takže karta, ktorú mal niekto otvorenú
 * pred nasadením, dostane pri ďalšom kliknutí 404 — a **nestane sa nič**:
 * obrazovka sa neotvorí a nikde nie je vysvetlenie prečo.
 *
 * Jediné rozumné riešenie je stránku načítať znova; nová verzia je na serveri
 * pripravená. Adresa sa zachová, takže používateľ skončí tam, kam klikol.
 */
router.onError((error, to) => {
	const message = error instanceof Error ? error.message : String(error)
	const isStaleChunk =
		/Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module/i.test(
			message,
		)

	if (isStaleChunk) {
		window.location.assign(to.fullPath)
	}
})

/**
 * Strata relácie (refresh tokenu neprešiel) končí na prihlásení s návratovou
 * adresou, aby používateľ po prihlásení pokračoval tam, kde skončil.
 */
onAuthLost(() => {
	const current = router.currentRoute.value
	if (current.name !== 'login') {
		void router.push({ name: 'login', query: { pokracovat: current.fullPath } })
	}
})
